// skillgenpro-frontend/src/error-framework/ErrorExperience.jsx
//
// The single reusable visual component for every error state in the app
// (Phase 11 of the framework spec: illustration/title/description/actions/
// error ID/timestamp, dark+light, responsive, accessible). Mount this once,
// e.g. right next to <ErrorProvider> in App.js — it renders nothing when
// there's no active error.
//
// Two presentations, chosen automatically from the error's severity/state:
//   - low-severity + currently auto-retrying: a small, non-blocking
//     Snackbar ("We're at capacity — retrying automatically...")
//   - everything else: a focused Dialog with the full contextual
//     explanation and action buttons, matching the tone of your prompt's
//     examples ("Your interview has been saved. AI evaluation will be
//     available once the service resumes.")

import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { useErrorHandler } from './ErrorContext';
import { getRegistryEntry } from './errorRegistry';

const ACTION_LABELS = {
  retry: 'Retry',
  reconnect: 'Reconnect',
  refresh: 'Refresh',
  login: 'Log In Again',
  support: 'Contact Support',
  dashboard: 'Continue',
  copyErrorId: 'Copy Error ID',
};

function buildActionHandlers({ retryActiveError, dismissError, activeError }) {
  return {
    retry: () => retryActiveError(),
    reconnect: () => retryActiveError(),
    refresh: () => {
      window.location.reload();
    },
    login: () => {
      window.location.href = '/login';
    },
    support: () => {
      const id = activeError ? activeError.errorId : '';
      window.location.href = `mailto:support@theskillgenie.com?subject=${encodeURIComponent('Error ' + id)}`;
    },
    // "Continue" — dismisses and lets the user keep going where they are.
    // Deliberately not a hardcoded route: per-role dashboard paths weren't
    // confirmed as part of this rollout, and a broken forced navigation is
    // worse than simply closing the dialog.
    dashboard: () => dismissError(),
    copyErrorId: () => {
      const id = activeError ? activeError.errorId : '';
      if (navigator.clipboard && id) {
        navigator.clipboard.writeText(id).catch(() => {});
      }
    },
  };
}

export default function ErrorExperience() {
  const { activeError, retryState, retryActiveError, dismissError } = useErrorHandler();

  // Auto-retry in progress and nothing has surfaced yet — small, calm,
  // non-blocking indicator instead of an interrupting dialog.
  if (retryState && !activeError) {
    const entry = getRegistryEntry(retryState.error && retryState.error.code, retryState.error && retryState.error.category);
    return (
      <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" variant="filled" sx={{ width: '100%' }}>
          {entry.title} — retrying automatically (attempt {retryState.attempt})…
        </Alert>
      </Snackbar>
    );
  }

  if (!activeError) return null;

  const entry = getRegistryEntry(activeError.code, activeError.category);
  const actions = entry.actions && entry.actions.length ? entry.actions : ['dashboard'];
  const handlers = buildActionHandlers({ retryActiveError, dismissError, activeError });

  return (
    <Dialog
      open
      onClose={dismissError}
      maxWidth="sm"
      fullWidth
      aria-labelledby="error-experience-title"
    >
      <DialogTitle id="error-experience-title">{entry.title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{activeError.userMessage}</DialogContentText>
        <Box sx={{ mt: 2, opacity: 0.6 }}>
          <Typography variant="caption" display="block">
            Error ID: {activeError.errorId}
          </Typography>
          <Typography variant="caption" display="block">
            {new Date(activeError.timestamp).toLocaleString()}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        {actions
          .filter((key) => key !== 'autoRetryIndicator')
          .map((key, idx) => (
            <Button
              key={key}
              onClick={handlers[key] || dismissError}
              variant={idx === 0 ? 'contained' : 'text'}
              autoFocus={idx === 0}
            >
              {ACTION_LABELS[key] || 'OK'}
            </Button>
          ))}
      </DialogActions>
    </Dialog>
  );
}
